// StepNoteModal — "Add Note" / "Edit Note" pop-up for a single Order Step.
// Writes to PUT /orders/:orderId/step-notes/:step.
// The server accepts { note: string | null } and returns the fresh order payload;
// the caller re-syncs its local state via onSaved(updatedOrder).

import { useState } from 'react';
import { Modal, Textarea, Button, Group, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { api } from '../../api/client';

interface Props {
  orderId: number;
  step: string;
  existingNote: string | null;
  onClose: () => void;
  onSaved: (updatedOrder: any) => void;
}

const MAX_CHARS = 500;

export default function StepNoteModal({ orderId, step, existingNote, onClose, onSaved }: Props) {
  const [note, setNote] = useState(existingNote ?? '');
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    try {
      const o = await api.put<any>(
        `/orders/${orderId}/step-notes/${encodeURIComponent(step)}`,
        { note: note.trim() || null },
      );
      onSaved(o);
      onClose();
    } catch (e: any) {
      notifications.show({ message: e.message ?? 'Save failed', color: 'red' });
    } finally {
      setSaving(false);
    }
  }

  async function clear() {
    setSaving(true);
    try {
      const o = await api.put<any>(
        `/orders/${orderId}/step-notes/${encodeURIComponent(step)}`,
        { note: null },
      );
      onSaved(o);
      onClose();
    } catch (e: any) {
      notifications.show({ message: e.message ?? 'Clear failed', color: 'red' });
    } finally {
      setSaving(false);
    }
  }

  const remaining = MAX_CHARS - note.length;

  return (
    <Modal
      opened
      onClose={onClose}
      title={<span style={{ fontWeight: 700 }}>{existingNote ? 'Edit Note' : 'Add Note'} — {step}</span>}
      size="md"
    >
      <Textarea
        data-tour="note-text"
        value={note}
        onChange={(e) => setNote(e.currentTarget.value.slice(0, MAX_CHARS))}
        placeholder="Write a note about this step…"
        minRows={5}
        autosize
        maxRows={12}
      />
      <Text size="xs" c={remaining < 50 ? 'red' : 'dimmed'} ta="right" mt={4}>
        {remaining} characters remaining
      </Text>
      <Group justify="space-between" mt="md">
        {existingNote && (
          <Button variant="subtle" color="red" size="compact-sm" onClick={clear} loading={saving}>
            Clear note
          </Button>
        )}
        <Group ml="auto" gap="sm">
          <Button variant="default" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button data-tour="note-save" onClick={save} loading={saving} disabled={note.trim() === (existingNote ?? '')}>
            Save
          </Button>
        </Group>
      </Group>
    </Modal>
  );
}
